import React, { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import GenericFileDropzone from '../../../../../../components/common/GenericFileDropzone';
import { Toast } from 'primereact/toast';

import toastService from '../../../../../../utils/toastService';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../../../api/endpoints/documentContent';
import DocumentDownloadService from '../../../../../../utils/documentDownloadService';
import { useLanguage } from '../../../../../../contexts/LanguageContext';

const VehicleRegistrationDocumentManager = ({ deleteVehicleRegistrationDocument, CVRFStatus, id, formData, documentList = [], uploadVehicleRegistrationDocument, updateVehicleRegistrationDocument, isDeleting, isUpdatingDocument, isUploading }) => {
    const [dialogVisible, setDialogVisible] = useState(false);
    const [newFiles, setNewFiles] = useState([]);
    const [description, setDescription] = useState('');
    const [editingDocument, setEditingDocument] = useState(null);

    const toast = useRef(null);
    const { th } = useLanguage();

    const [triggerDownloadDocumentByKey] = useLazyDownloadDocumentByKeyQuery();
    const [downloadService] = useState(() => new DocumentDownloadService(toast));

    const isFormEditable = formData?.status !== CVRFStatus.Approved && formData?.status !== CVRFStatus.SentForApproval;

    const handleUpload = async () => {
        if (!newFiles.length && !editingDocument) {
            toast.current?.show({
                severity: 'warn',
                summary: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.warnSummary'),
                detail: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.selectFile'),
                life: 3000
            });
            return;
        }

        if (editingDocument) {
            const payload = {
                vehicleRegistrationFormId: id,
                documentId: editingDocument.id,
                description: description || editingDocument.description || editingDocument.title
            };

            if (newFiles.length > 0) {
                payload.file = newFiles[0];
            }

            const success = await updateVehicleRegistrationDocument(payload);

            if (success) {
                toast.current?.show({
                    severity: 'success',
                    summary: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.successSummary'),
                    detail: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.fileUpdated'),
                    life: 3000
                });
                setDialogVisible(false);
                setEditingDocument(null);
                setNewFiles([]);
                setDescription('');
            }
        } else {
            if (!newFiles.length) {
                toast.current?.show({
                    severity: 'warn',
                    summary: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.warnSummary'),
                    detail: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.selectFile'),
                    life: 3000
                });
                return;
            }

            const success = await uploadVehicleRegistrationDocument({
                vehicleRegistrationFormId: id,
                file: newFiles[0],
                description: description || newFiles[0].name
            });

            if (success) {
                toast.current?.show({
                    severity: 'success',
                    summary: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.successSummary'),
                    detail: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.fileUploaded'),
                    life: 3000
                });
                setDialogVisible(false);
                setNewFiles([]);
                setDescription('');
            }
        }
    };

    const handleDownloadDocument = async (fileKey) => {
        toast.current?.show({
            severity: 'info',
            summary: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.downloadingSummary'),
            detail: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.downloadingDetail'),
            life: 2000
        });

        try {
            await downloadService.downloadByKey(triggerDownloadDocumentByKey, fileKey, null, {
                successMessage: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.downloadSuccess'),
                errorMessage: th('warranty.dealerVehicleRegistrationForm.documentManager.toasts.downloadError')
            });
        } catch (error) {
            console.error('Dosya indirme hatası:', error);
        }
    };

    const actionBodyTemplate = (doc) => (
        <div className="flex gap-3">
            <i
                className={`pi pi-pencil cursor-pointer text-yellow-500 ${!isFormEditable ? 'text-400' : ''}`}
                style={{ opacity: !isFormEditable ? 0.5 : 1 }}
                title={th('warranty.dealerVehicleRegistrationForm.buttons.edit')}
                onClick={() => {
                    if (!isFormEditable) return;
                    setEditingDocument(doc);
                    setDescription(doc.description || doc.title || doc.fileName);
                    setNewFiles([]);
                    setDialogVisible(true);
                }}
            />
            <i className="pi pi-download cursor-pointer" title={th('warranty.dealerVehicleRegistrationForm.documentManager.buttons.download')} onClick={() => handleDownloadDocument(doc.fileKey)} />
            <i
                className={`pi pi-trash cursor-pointer ${!isFormEditable ? 'text-400' : ''}`}
                style={{ color: !isFormEditable ? '#999' : 'red', opacity: !isFormEditable ? 0.5 : 1 }}
                title={th('warranty.dealerVehicleRegistrationForm.buttons.delete')}
                onClick={() => {
                    if (!isFormEditable || !doc.id) return;
                    deleteVehicleRegistrationDocument(id, doc.id);
                }}
            />
        </div>
    );

    const isBusy = isUploading || isUpdatingDocument || isDeleting;

    return (
        <div className="col-12">
            <Toast ref={toast} />

            <div className="flex justify-content-end mb-3">
                <Button icon="pi pi-plus" label={th('warranty.dealerVehicleRegistrationForm.documentManager.buttons.addFile')} className="p-button-success" onClick={() => setDialogVisible(true)} disabled={!isFormEditable} />
            </div>

            <Dialog
                header={editingDocument ? th('warranty.dealerVehicleRegistrationForm.documentManager.dialog.updateHeader') : th('warranty.dealerVehicleRegistrationForm.documentManager.dialog.addHeader')}
                visible={dialogVisible}
                style={{ width: '40vw' }}
                onHide={() => {
                    setDialogVisible(false);
                    setEditingDocument(null);
                    setNewFiles([]);
                    setDescription('');
                }}
                modal
            >
                {isBusy && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            background: '#E8F0FE',
                            border: '1px solid #A7C7E7',
                            borderRadius: '8px',
                            padding: '8px 16px',
                            marginBottom: '1rem',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}
                    >
                        <span style={{ fontWeight: 500, color: '#0B3187' }}>{th('warranty.dealerVehicleRegistrationForm.documentManager.dialog.processing')}</span>
                        <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="4" animationDuration=".8s" />
                    </div>
                )}

                {editingDocument && (
                    <div className="flex align-items-center gap-3 border-1 surface-border p-3 border-round mb-3">
                        <i className="pi pi-file" style={{ fontSize: '1.5rem', color: '#0B3187' }}></i>
                        <div className="flex flex-column">
                            <span className="font-medium">{editingDocument.title || editingDocument.fileName}</span>
                            <small className="text-color-secondary">
                                {editingDocument.fileType?.toUpperCase()} • {Math.round(editingDocument.fileSize / 1024)} KB
                            </small>
                        </div>
                    </div>
                )}

                <GenericFileDropzone files={newFiles} onFilesChange={setNewFiles} multiple={false} maxFileSize={20 * 1024 * 1024} disabled={isBusy} />

                <div className="mt-4">
                    <label className="block mb-2 font-medium">{th('warranty.dealerVehicleRegistrationForm.documentManager.dialog.descriptionLabel')}</label>
                    <InputText value={description} onChange={(e) => setDescription(e.target.value)} className="w-full" placeholder={th('warranty.dealerVehicleRegistrationForm.documentManager.dialog.descriptionPlaceholder')} disabled={isBusy} />
                </div>

                <div className="flex justify-content-end mt-4">
                    <Button label={th('warranty.dealerVehicleRegistrationForm.buttons.cancel')} className="p-button-text" onClick={() => setDialogVisible(false)} disabled={isBusy} />
                    <Button label={th('warranty.dealerVehicleRegistrationForm.buttons.save')} className="p-button-primary ml-2" onClick={handleUpload} disabled={isBusy} loading={isBusy} />
                </div>
            </Dialog>

            {documentList.length > 0 && (
                <DataTable value={documentList} stripedRows showGridlines responsiveLayout="scroll" className="mt-4" emptyMessage={th('warranty.dealerVehicleRegistrationForm.documentManager.table.emptyMessage')}>
                    <Column header={th('warranty.dealerVehicleRegistrationForm.table.actions')} body={actionBodyTemplate} style={{ width: '120px', textAlign: 'left' }} />
                    <Column
                        header={th('warranty.dealerVehicleRegistrationForm.documentManager.table.fileDescription')}
                        body={(doc) => (
                            <div className="flex align-items-center gap-3">
                                <i className="pi pi-file" style={{ fontSize: '1.5rem', color: '#0B3187' }}></i>
                                <div className="flex flex-column">
                                    <span className="font-medium">{doc.title || doc.fileName}</span>
                                    <small className="text-color-secondary">
                                        {doc.fileType?.toUpperCase()} • {Math.round(doc.fileSize / 1024)} KB
                                    </small>
                                </div>
                            </div>
                        )}
                    />
                </DataTable>
            )}
        </div>
    );
};

export default VehicleRegistrationDocumentManager;
